import React, { useState, useEffect } from 'react';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import { AnimatePresence } from 'framer-motion';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import HeaderMobile from './components/HeaderMobile';
import AdminPage from './components/AdminPage';
import MainLoginPage from './components/MainLoginPage';
import Hero from './components/Hero';
import HeroMobile from './components/HeroMobile';
import About from './components/About';
import Services from './components/Services';
import ServicesMobile from './components/ServicesMobile';
import DiscoverProducts from './components/DiscoverProducts';
import CableTransportCategory from './components/CableTransportCategory';
import TransformateursCategory from './components/TransformateursCategory';
import CablesBatimentsCategory from './components/CablesBatimentsCategory';
import ConducteursAAACDetail from './components/ConducteursAAACDetail';
import CableMTDetail from './components/CableMTDetail';
import CableMTEDRDetail from './components/CableMTEDRDetail';
import NOVAREPoteauDetail from './components/NOVAREPoteauDetail';
import CableMT1220Detail from './components/CableMT1220Detail';
import ConducteursAACSRDetail from './components/ConducteursAACSRDetail';
import CuivreNuEcrouiDetail from './components/CuivreNuEcrouiDetail';
import TorsadeBranchementDetail from './components/TorsadeBranchementDetail';
import NOVARECabineDetail from './components/NOVARECabineDetail';
import NOVAREPowerDetail from './components/NOVAREPowerDetail';
import Markets from './components/Markets';
import MarketsMobile from './components/MarketsMobile';
import Partners from './components/Partners';
import EclairagePublicCategory from './components/EclairagePublicCategory';
import EclairagePublicEnsembleDetail from './components/EclairagePublicEnsembleDetail';
import EclairagePublicSubcategory from './components/EclairagePublicSubcategory';
import Contact from './components/Contact';
import Stats from './components/Stats';
import TrustedClients from './components/TrustedClients';
import Footer from './components/Footer';
import ProductDetailRouter from './components/ProductDetailRouter';
import AllProducts from './components/AllProducts';
import CategoryProducts from './components/CategoryProducts';
import { translations } from './translations';
import InstallationService from './components/InstallationService';
import InstallationServiceMobile from './components/InstallationServiceMobile';
import CellulesHTACategory from './components/CellulesHTACategory';
import CellulesFluocellDetail from './components/CellulesFluocellDetail';
import PostesPrefabriquesCategory from './components/PostesPrefabriquesCategory';
import ClipperC32C37Detail from './components/ClipperC32C37Detail';
import ClipperC32C37DetailMobile from './components/ClipperC32C37DetailMobile';
import AltoADetail from './components/AltoADetail';
import AltoADetailMobile from './components/AltoADetailMobile';
import BocageDetail from './components/BocageDetail';
import BocageDetailMobile from './components/BocageDetailMobile';
import GroupesElectrogenesCategory from './components/GroupesElectrogenesCategory';
import MaterielElectriqueCategory from './components/MaterielElectriqueCategory';
import CataloguePage from './components/CataloguePage';
import MaterielElectriqueDetail from './components/MaterielElectriqueDetail';
import MaterielElectriqueSubcategory from './components/MaterielElectriqueSubcategory';
import SolutionsPage from './components/SolutionsPage';
import SolairePage from './components/SolairePage';
import SolairePageMobile from './components/SolairePageMobile';
import MonitoringPage from './components/MonitoringPage';
import MonitoringPageMobile from './components/MonitoringPageMobile';



interface GlobalStyleProps {
  $isRTL: boolean;
}

const GlobalStyle = createGlobalStyle<GlobalStyleProps>`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    font-family: ${props => props.$isRTL ? 
      "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
      "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
    line-height: 1.6;
    color: #333;
    direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
    overflow-x: hidden;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  ul {
    list-style: none;
  }

  button {
    border: none;
    background: none;
    cursor: pointer;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  .loading {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    font-size: 1.2rem;
    color: #666;
  }
`;

const AppContainer = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;
const theme = {
  colors: {
    primary: '#dc2626',
    primaryDark: '#b91c1c',
    secondary: '#1e293b',
    accent: '#f59e0b',
    white: '#ffffff',
    gray: '#6b7280',
    lightGray: '#f3f4f6',
    darkGray: '#374151'
  },
  fonts: {
    primary: 'Inter, sans-serif'
  },
  breakpoints: {
    mobile: '768px',
    tablet: '1024px',
    desktop: '1200px'
  }
};
function App() {
  // Load language from localStorage or default to 'fr'
  const [language, setLanguage] = useState<'fr' | 'en' | 'ar'>(() => {
    const savedLanguage = localStorage.getItem('techyid-language') as 'fr' | 'en' | 'ar';
    return savedLanguage || 'fr';
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const t = translations[language];
  const isRTL = language === 'ar';

  // Save language to localStorage whenever it changes
  const handleLanguageChange = (newLanguage: 'fr' | 'en' | 'ar') => {
    setLanguage(newLanguage);
    localStorage.setItem('techyid-language', newLanguage);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
  };

  const hasActiveFilters = searchTerm !== '' || selectedCategory !== '';

  useEffect(() => {
    document.documentElement.lang = language;
    document.dir = isRTL ? 'rtl' : 'ltr';
  }, [language, isRTL]);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle $isRTL={isRTL} />
      <Router>
      <AppContainer $isRTL={isRTL}>
        {isMobile ? (
            <HeaderMobile 
              language={language} 
              setLanguage={handleLanguageChange} 
              t={t} 
              isRTL={isRTL} 
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              viewMode={viewMode}
              setViewMode={setViewMode}
              onClearFilters={clearFilters}
              hasActiveFilters={hasActiveFilters}
            />
          ) : (
            <Header 
              language={language} 
              setLanguage={handleLanguageChange} 
              t={t} 
              isRTL={isRTL} 
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              viewMode={viewMode}
              setViewMode={setViewMode}
              onClearFilters={clearFilters}
              hasActiveFilters={hasActiveFilters}
            />
          )}

          <AnimatePresence mode="wait">
            <Routes>
              <Route path="/admin" element={<AdminPage t={t} isRTL={isRTL} />} />
              <Route path="/login" element={<MainLoginPage t={t} isRTL={isRTL} />} />
              <Route path="/solutions" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><SolutionsPage t={t} isRTL={isRTL} /></>} />
              <Route path="/product/:id" element={<ProductDetailRouter />} />
              <Route path="/products" element={
                <>
                  <AllProducts 
                    t={t} 
                    isRTL={isRTL} 
                    language={language} 
                    setLanguage={handleLanguageChange}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    onClearFilters={clearFilters}
                    hasActiveFilters={hasActiveFilters}
                  />
                </>
              } />
              <Route path="/category/cables-transport" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><CableTransportCategory t={t} isRTL={isRTL} /></>} />

        <Route path="/category/cables-batiments" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><CablesBatimentsCategory t={t} isRTL={isRTL} /></>} />
              <Route path="/product/cuivre-nu-ecroui" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><CuivreNuEcrouiDetail t={t} isRTL={isRTL} /></>} />
                <Route path="/product/conducteurs-aaac" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><ConducteursAAACDetail t={t} isRTL={isRTL} /></>} />
<Route path="/product/cable-mt-nfc" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><CableMTDetail t={t} isRTL={isRTL} /></>} />
<Route path="/product/cable-mt-12-20-edr" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><CableMTEDRDetail t={t} isRTL={isRTL} /></>} />

        <Route path="/product/cable-mt-12-20" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><CableMT1220Detail t={t} isRTL={isRTL} /></>} />

<Route path="/product/torsade-branchement" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><TorsadeBranchementDetail t={t} isRTL={isRTL} /></>} />
                <Route path="/product/conducteurs-aacsr" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><ConducteursAACSRDetail t={t} isRTL={isRTL} /></>} />
              <Route path="/category/:categoryId" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><CategoryProducts t={t} isRTL={isRTL} language={language} setLanguage={handleLanguageChange} /></>} />
              <Route path="/installation-service" element={
                <>
                  {isMobile ? (
                    <HeaderMobile language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} searchTerm="" setSearchTerm={() => {}} selectedCategory="" setSelectedCategory={() => {}} viewMode="grid" setViewMode={() => {}} onClearFilters={() => {}} hasActiveFilters={false} />
                  ) : (
                    <Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} />
                  )}
                  {isMobile ? (
                    <InstallationServiceMobile t={t} isRTL={isRTL} />
                  ) : (
                    <InstallationService t={t} isRTL={isRTL} />
                  )}
                </>
              } />
              <Route path="/" element={
                <>
                  {isMobile ? (
                    <HeroMobile t={t} isRTL={isRTL} language={language} />
                  ) : (
                  <Hero t={t} isRTL={isRTL} language={language} />
                  )}
                  <About t={t} isRTL={isRTL} />
                  <DiscoverProducts t={t} isRTL={isRTL} />
                  {isMobile ? (
                    <ServicesMobile t={t} isRTL={isRTL} />
                  ) : (
                  <Services t={t} isRTL={isRTL} />
                  )}
                  {isMobile ? (
                    <MarketsMobile t={t} isRTL={isRTL} />
                  ) : (
                  <Markets t={t} isRTL={isRTL} />
                  )}
                  <Partners t={t} isRTL={isRTL} />
                  <Stats t={t} isRTL={isRTL} />
                  <TrustedClients t={t} isRTL={isRTL} />
                  <Contact t={t} isRTL={isRTL} />
                  <Footer t={t} isRTL={isRTL} />
                </>
              } />
              <Route path="/category/cables-transport" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><CableTransportCategory t={t} isRTL={isRTL} /></>} />
              <Route path="/category/transformateurs" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><TransformateursCategory t={t} isRTL={isRTL} /></>} />
              <Route path="/category/cables-batiments" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><CablesBatimentsCategory t={t} isRTL={isRTL} /></>} />
              <Route path="/product/novare-poteau" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><NOVAREPoteauDetail t={t} isRTL={isRTL} /></>} />
              <Route path="/product/cuivre-nu-ecroui" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><CuivreNuEcrouiDetail t={t} isRTL={isRTL} /></>} />
              <Route path="/product/novare-cabine" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><NOVARECabineDetail t={t} isRTL={isRTL} /></>} />
              <Route path="/product/novare-poteau" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><NOVAREPoteauDetail t={t} isRTL={isRTL} /></>} />
              <Route path="/product/novare-power" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><NOVAREPowerDetail t={t} isRTL={isRTL} /></>} />
              <Route path="/product/novare-cabine" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><NOVARECabineDetail t={t} isRTL={isRTL} /></>} />
              <Route path="/category/cellules-hta" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><CellulesHTACategory t={t} isRTL={isRTL} /></>} />
              <Route path="/category/cables-transport" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><CableTransportCategory t={t} isRTL={isRTL} /></>} />
              <Route path="/product/cellules-fluocell" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><CellulesFluocellDetail t={t} isRTL={isRTL} /></>} />
              <Route path="/product/novare-power" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><NOVAREPowerDetail t={t} isRTL={isRTL} /></>} />
              <Route path="/category/postes-prefabriques" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><PostesPrefabriquesCategory t={t} isRTL={isRTL} /></>} />
              <Route path="/category/cellules-hta" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><CellulesHTACategory t={t} isRTL={isRTL} /></>} />
              <Route path="/product/clipper-c32-c37" element={
                <>
                  <Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} />
                  {isMobile ? (
                    <ClipperC32C37DetailMobile t={t} isRTL={isRTL} />
                  ) : (
                    <ClipperC32C37Detail t={t} isRTL={isRTL} />
                  )}
                </>
              } />
              <Route path="/product/cellules-fluocell" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><CellulesFluocellDetail t={t} isRTL={isRTL} /></>} />
              <Route path="/product/alto-a" element={
                <>
                  <Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} />
                  {isMobile ? (
                    <AltoADetailMobile t={t} isRTL={isRTL} />
                  ) : (
                    <AltoADetail t={t} isRTL={isRTL} />
                  )}
                </>
              } />
              <Route path="/product/bocage" element={
                <>
                  <Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} />
                  {isMobile ? (
                    <BocageDetailMobile t={t} isRTL={isRTL} />
                  ) : (
                    <BocageDetail t={t} isRTL={isRTL} />
                  )}
                </>
              } />
              <Route path="/category/groupes-electrogenes" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><GroupesElectrogenesCategory t={t} isRTL={isRTL} /></>} />
              <Route path="/category/materiel-electrique" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><MaterielElectriqueCategory t={t} isRTL={isRTL} /></>} />
              <Route path="/category/eclairage-public" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><EclairagePublicCategory t={t} isRTL={isRTL} /></>} />
              <Route path="/category/eclairage-public/subcategory/:subcategoryId" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><EclairagePublicSubcategory t={t} isRTL={isRTL} /></>} />
              <Route path="/category/eclairage-public/:ensembleId" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><EclairagePublicEnsembleDetail t={t} isRTL={isRTL} /></>} />
              <Route path="/category/materiel-electrique" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><MaterielElectriqueDetail t={t} isRTL={isRTL} /></>} />
              <Route path="/category/materiel-electrique/:subcategoryId" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><MaterielElectriqueSubcategory t={t} isRTL={isRTL} /></>} />
              <Route path="/catalogue" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><CataloguePage t={t} isRTL={isRTL} /></>} />
              <Route path="/category/groupes-electrogenes" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><GroupesElectrogenesCategory t={t} isRTL={isRTL} /></>} />
              <Route path="/category/materiel-electrique" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><MaterielElectriqueCategory t={t} isRTL={isRTL} /></>} />
              <Route path="/category/eclairage-public" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><EclairagePublicCategory t={t} isRTL={isRTL} /></>} />
              <Route path="/category/eclairage-public/subcategory/:subcategoryId" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><EclairagePublicSubcategory t={t} isRTL={isRTL} /></>} />
              <Route path="/category/eclairage-public/:ensembleId" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><EclairagePublicEnsembleDetail t={t} isRTL={isRTL} /></>} />
              <Route path="/solutions" element={<><Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} /><SolutionsPage t={t} isRTL={isRTL} /></>} />
              <Route path="/solaire" element={
                <>
                  {isMobile ? (
                    <HeaderMobile language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} searchTerm="" setSearchTerm={() => {}} selectedCategory="" setSelectedCategory={() => {}} viewMode="grid" setViewMode={() => {}} onClearFilters={() => {}} hasActiveFilters={false} />
                  ) : (
                    <Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} />
                  )}
                  {isMobile ? (
                    <SolairePageMobile t={t} isRTL={isRTL} />
                  ) : (
                    <SolairePage t={t} isRTL={isRTL} />
                  )}
                </>
              } />
              <Route path="/monitoring" element={
                <>
                  {isMobile ? (
                    <HeaderMobile language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} searchTerm="" setSearchTerm={() => {}} selectedCategory="" setSelectedCategory={() => {}} viewMode="grid" setViewMode={() => {}} onClearFilters={() => {}} hasActiveFilters={false} />
                  ) : (
                    <Header language={language} setLanguage={handleLanguageChange} t={t} isRTL={isRTL} showOnlyHome={true} />
                  )}
                  {isMobile ? (
                    <MonitoringPageMobile t={t} isRTL={isRTL} />
                  ) : (
                    <MonitoringPage t={t} isRTL={isRTL} />
                  )}
                </>
              } />
            </Routes>
          </AnimatePresence>
        </AppContainer>
        </Router>
    </ThemeProvider>
  );
}

export default App;
